import React, { useEffect, useState, useRef } from 'react';
import { API, Auth } from '../utils/amplifyWrapper';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import Spinner from './Spinner';
import { fetchIpInfoService } from '../utils/Country';
import { getFirstTouch } from '../utils/FirstTouch';
import { getSignupSource } from '../utils/SignupSource';
import { getGa4Attribution } from '../utils/Ga4Client';
import { getMpDistinctId } from '../utils/mpIdentity';
import { trackLoginOncePerDay, markLoginTrackedForToday } from '../services/mixpanel';
import { trackGA4EventAsync, setGA4UserProperties, gateRedirectOnGA4Events } from '../utils/trackGA4Event';
import { getGa4PlanTier } from '../utils/ga4PlanTier';
import { EMAIL_VERIFIED, PLAN_SELECTED, SIGN_UP_COMPLETE } from '../constants/gtag-events';
import PLANS from '../constants/plans.json';
import { ACCOUNT_CREATED } from '../constants/hotjar-events';
import { useAuthStore } from '../zustand-store/useAuthStore';
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';

/**
 * Helper: retry getting the authenticated user.
 * Amplify processes the OAuth code during Amplify.configure() in index.js,
 * which runs BEFORE React renders. By the time this component mounts the
 * tokens are usually ready, but we retry a few times just in case.
 */
async function waitForAuthenticatedUser(maxAttempts = 6, delayMs = 1500) {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            console.log(`🔄 Attempt ${attempt}/${maxAttempts} - getting authenticated user`);
            const currentUser = await getCurrentUser();
            const attributes = await fetchUserAttributes();
            console.log('✅ Got authenticated user:', currentUser.username);
            return {
                username: currentUser.username,
                userId: currentUser.userId,
                signInDetails: currentUser.signInDetails,
                attributes: attributes,
            };
        } catch (err) {
            console.log(`⏳ Attempt ${attempt} failed:`, err.message || err);
            if (attempt < maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, delayMs));
            }
        }
    }
    throw new Error('Could not retrieve authenticated user after OAuth callback');
}

export default function GoogleCallback() {
    const [status, setStatus] = useState('Processing Google sign-in...');
    const history = useHistory();
    const { setUser, setAuthState } = useAuthStore();
    // Use ref instead of local variable so it survives React StrictMode unmount/remount
    const isMountedRef = useRef(true);
    const isProcessingRef = useRef(false);

    useEffect(() => {
        // Reset ref on each mount (StrictMode re-mount will set this back to true)
        isMountedRef.current = true;
        
        // Prevent multiple processing
        if (isProcessingRef.current) {
            console.log('⏭️  SKIPPING - already processing');
            return;
        }
        
        isProcessingRef.current = true;

        // Shared by every recovery path below (OAuth error, user-setup failure,
        // OAuth polling failure, GA4-gate failure): show the failure, then bounce
        // back to sign-in after a delay so the toast/status has time to render.
        const recoverToSignIn = (statusMessage, { toastMessage, delay = 2000, toastOptions } = {}) => {
            if (toastMessage) {
                toast.error(toastMessage, toastOptions);
            }
            if (isMountedRef.current) {
                setStatus(statusMessage);
                setTimeout(() => {
                    if (isMountedRef.current) {
                        history.push('/');
                        setAuthState('signin');
                    }
                }, delay);
            }
        };

        const urlParams = new URLSearchParams(window.location.search);
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');
        
        if (error) {
            console.error('OAuth error:', error, errorDescription);
            // Keep isProcessingRef.current = true to prevent StrictMode re-mount from retrying
            
            recoverToSignIn('Authentication error. Redirecting...', {
                toastMessage: 'Please use the authentication method you used during signup (email/password or Google).',
                toastOptions: { autoClose: 5000, position: 'top-center' },
            });
            return;
        }

        const handleGoogleUserSetup = async (user) => {
            try {
                console.log('📋 handleGoogleUserSetup started');
                if (isMountedRef.current) setStatus('Checking existing account...');
                
                try {
                    const orgResponse = await API.get("main", "/organisation");
                    
                    if (orgResponse.data.length > 0) {
                        console.log('👤 EXISTING USER - SETTING USER AND REDIRECTING');
                        setUser(user);
                        // GROW-81: Login event for a returning Google user (deduped to once/day,
                        // shared with the email and session-resume paths so the post-callback
                        // app remount doesn't double-count this sign-in).
                        trackLoginOncePerDay({ method: 'google' });
                        if (isMountedRef.current) setStatus('Welcome back! Redirecting...');
                        
                        isProcessingRef.current = false;
                        history.push('/');
                        setAuthState('signedin');
                        return;
                    }
                } catch (orgError) {
                    console.log('📋 Org check failed, treating as new user:', orgError.message);
                    // Treat as new user
                }
                
                // New Google user - complete signup flow.
                // GROW-81: new account, not a return — consume today's login slot so the
                // session-resume path doesn't count signup day as a Login.
                markLoginTrackedForToday();
                await completeGoogleUserSignup(user);
                
            } catch (error) {
                console.error('❌ Error in Google callback:', error);
                isProcessingRef.current = false;
                recoverToSignIn(`Authentication failed: ${error.message}`, { delay: 3000 });
            }
        };

        const completeGoogleUserSignup = async (user) => {
            try {
                if (isMountedRef.current) setStatus('Setting up your account...');
                
                const userLocationData = await fetchIpInfoService();
                const location = {
                    country: userLocationData.country,
                };

                const storedFirstTouch = getFirstTouch();
                const firstTouch = {
                    ...storedFirstTouch,
                    first_country: storedFirstTouch?.first_country || location.country,
                };

                const firstName = user.attributes.given_name || user.attributes.name?.split(' ')[0] || 'User';
                const localOrganisation = `${firstName}'s organisation`;
                
                if (isMountedRef.current) setStatus('Creating your organization...');
                
                await Auth.updateUserAttributes(user, {
                    'custom:org': localOrganisation,
                    'custom:phone': '',
                    'custom:usecase': '',
                    'custom:referrer_url': window.document.referrer,
                    'custom:is_v2': '1'
                });
                
                const response = await API.post(`main`, `/organisation`, {
                    body: { name: localOrganisation, location, source: 'google', firstTouch, ...getGa4Attribution(), mp_distinct_id: getMpDistinctId() },
                    headers: { "Content-Type": "application/json" },
                });
                
                if (!response.success) {
                    throw new Error(`Organization creation failed: ${response.error || 'Unknown error'}`);
                }
                
                window.hj('identify', response.data.orgId, {
                    'Page': 'Google Sign up',
                });

                // federatedSignIn doesn't carry a pre-selected plan through the OAuth
                // redirect, so the tier defaults to free — but plan_id should still be sent.
                const signupSource = storedFirstTouch?.first_landing_page || storedFirstTouch?.first_referrer || window.document.referrer || 'direct';
                const freePlan = PLANS.find((p) => p.plan_family === 'free');
                const freePlanId = location.country === 'IN' ? freePlan?.plan_id_inr : freePlan?.plan_id_usd;
                const ga4PlanTier = getGa4PlanTier(freePlanId);

                window.hj("event", ACCOUNT_CREATED);

                setGA4UserProperties({
                    organisation_id: response.data.orgId,
                    user_plan_tier: ga4PlanTier,
                    user_type: 'free',
                    signup_month: new Date().toISOString().slice(0, 7),
                    first_touch_source: storedFirstTouch?.first_utm_source || storedFirstTouch?.first_referring_domain || 'direct',
                });

                if (isMountedRef.current) setStatus('Account created! Redirecting...');

                // Do NOT call setUser/setAuthState here — it would cause App.js to
                // unmount the auth layout (showing ProtectedRoutes) while the URL is
                // still /google/callback, resulting in a 404 flash.
                // The full page reload via window.location.href will trigger App.js
                // to re-detect the authenticated user and set state correctly.
                console.log('🔄 NEW USER REDIRECT: /onboarding/work');

                // Fire all three events in parallel and gate the redirect on all of
                // them together — nesting them serially stacks each call's own
                // ~1000ms fallback timeout on top of the others. Keep
                // isProcessingRef.current = true until we actually navigate away (or
                // give up and recover), so a remount during this window can't
                // re-trigger the signup flow.
                gateRedirectOnGA4Events([
                    trackGA4EventAsync(SIGN_UP_COMPLETE, { method: 'google', signup_source: signupSource, ...getSignupSource() }),
                    trackGA4EventAsync(EMAIL_VERIFIED, { method: 'google' }),
                    trackGA4EventAsync(PLAN_SELECTED, { change_type: 'initial', plan_id: freePlanId, plan_tier: ga4PlanTier }),
                ], 'GA4 signup event(s) did not confirm before Google signup redirect', () => {
                    isProcessingRef.current = false;
                    // User may have navigated away while these were in flight — don't
                    // force a redirect on a page they already left.
                    if (isMountedRef.current) {
                        window.location.href = `/onboarding/work?orgId=${response.data.orgId}&integrationId=${response.data.id}`;
                    }
                }, (error) => {
                    console.error('Error setting up Google user:', error);
                    isProcessingRef.current = false;
                    // Match processOAuth's catch below: surface the failure and
                    // recover instead of leaving the user stuck on "Redirecting...".
                    recoverToSignIn('Authentication failed. Redirecting...', {
                        toastMessage: 'Google authentication failed. Please try again.',
                    });
                });

            } catch (error) {
                console.error('Error setting up Google user:', error);
                throw error;
            }
        };

        // Amplify processes the OAuth authorization code during Amplify.configure()
        // in index.js, which runs BEFORE React renders. By the time this component
        // mounts, the token exchange is usually already done. We poll with retries
        // to handle any edge-case timing issues.
        const processOAuth = async () => {
            try {
                if (isMountedRef.current) setStatus('Verifying authentication...');
                const user = await waitForAuthenticatedUser();
                console.log('🚀 User retrieved, calling handleGoogleUserSetup');
                await handleGoogleUserSetup(user);
            } catch (err) {
                console.error('❌ Failed to get user after OAuth:', err);
                isProcessingRef.current = false;
                recoverToSignIn('Authentication failed. Redirecting...', {
                    toastMessage: 'Google authentication failed. Please try again.',
                });
            }
        };

        processOAuth();
        
        return () => {
            // Do NOT set isMountedRef to false here — StrictMode cleanup
            // would kill the in-flight async operation. The ref persists
            // across the unmount/remount cycle.
        };
    }, [history, setUser, setAuthState]);

    // Separate cleanup for actual unmount (e.g., navigation away)
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            isProcessingRef.current = false;
        };
    }, []);

    return (
        <div className="row my-5">
            <div className="col-md-6 offset-md-3 text-center">
                <div style={{ width: 150, height: 6, borderRadius: 10, background: 'var(--bs-border-color)', margin: '0 auto 20px' }}>
                    <div style={{ height: '100%', background: 'var(--bs-primary)', borderRadius: 10, transition: 'width 0.3s ease', width: '33%' }} />
                </div>
                <h4 className="fw-bold text-body-emphasis mb-1">Welcome to Listen2It!</h4>
                <p className="mb-5" style={{ color: 'var(--bs-secondary-color)', fontSize: 14 }}>
                    {status}
                </p>
                <div className="d-flex justify-content-center">
                    <Spinner color="var(--bs-primary)" />
                </div>
            </div>
        </div>
    );
}